import { AppShell, Box, Divider, NavLink, Select, Skeleton, Text, TextInput } from '@mantine/core';
import {
    IconChartBar,
    IconCoin,
    IconLink,
    IconMessage,
    IconPhoto,
    IconSettings,
    IconStack2,
} from '@tabler/icons-react';
import React from 'react';
import { Link, useLocation, useMatch, useNavigate, useParams } from 'react-router';

import { useGames } from '../data/games';
import * as remote from '../data/remote-data';

interface SectionLink {
    path: string;
    label: string;
    icon: React.ComponentType<{ size?: number | string }>;
}

interface SectionGroup {
    title: string;
    sections: SectionLink[];
}

const SECTION_GROUPS: SectionGroup[] = [
    {
        title: 'Manage',
        sections: [
            { path: 'campaigns', label: 'Campaigns', icon: IconStack2 },
            { path: 'creatives', label: 'Creatives', icon: IconPhoto },
            { path: 'line-media', label: 'LINE Media', icon: IconMessage },
            { path: 'ad-control', label: 'Ad Control Logic', icon: IconSettings },
        ],
    },
    {
        title: 'Analyze',
        sections: [
            { path: 'reports', label: 'Reports', icon: IconChartBar },
            { path: 'roas', label: 'ROAS', icon: IconCoin },
        ],
    },
    {
        title: 'Utilities',
        sections: [{ path: 'line-url-generator', label: 'LINE URL Generator', icon: IconLink }],
    },
];

const NavItem = ({ section, gameId, onClick }: { section: SectionLink; gameId: string; onClick: () => void }) => {
    const path = `/games/${gameId}/${section.path}`;
    const match = useMatch(path);
    const Icon = section.icon;

    return (
        <NavLink
            component={Link}
            to={path}
            label={section.label}
            leftSection={<Icon size={16} />}
            active={!!match}
            onClick={onClick}
            variant="light"
        />
    );
};

export const Sidebar = ({ onNavItemClick }: { onNavItemClick?: () => void }) => {
    const games = useGames();
    const navigate = useNavigate();
    const location = useLocation();
    const { id: currentGameId } = useParams();

    const handleGameChange = (gameId: string | null) => {
        if (!gameId) return;
        const pathSegments = location.pathname.split('/');
        const currentSection = pathSegments[3];
        const section = currentSection || 'campaigns';

        void navigate(`/games/${gameId}/${section}`);
        onNavItemClick?.();
    };

    const handleNavItemClick = () => {
        onNavItemClick?.();
    };

    return (
        <>
            {/* Game Switcher */}
            <AppShell.Section px="md" py="lg">
                {remote.matchAll(games, {
                    NotAsked: () => null,
                    Success: (gamesList) => (
                        <Select
                            value={currentGameId || null}
                            onChange={handleGameChange}
                            data={gamesList.map((game) => ({
                                value: game.id,
                                label: game.name,
                            }))}
                            styles={{
                                input: {
                                    fontWeight: 600,
                                },
                            }}
                        />
                    ),
                    Loading: () => <Skeleton height={36} radius="sm" animate />,
                    Failure: () => <TextInput disabled value="" error="Error loading games" />,
                })}
            </AppShell.Section>

            {/* Section Navigation */}
            {currentGameId ? (
                <>
                    <Divider mx="md" />
                    <AppShell.Section grow component="nav" style={{ overflowY: 'auto' }}>
                        {SECTION_GROUPS.map((group, groupIndex) => (
                            <Box key={group.title}>
                                <Box px="md" py="sm" pt={groupIndex === 0 ? 'md' : 'sm'}>
                                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.1em">
                                        {group.title}
                                    </Text>
                                </Box>
                                <Box px="xs">
                                    {group.sections.map((section) => (
                                        <NavItem
                                            key={section.path}
                                            section={section}
                                            gameId={currentGameId}
                                            onClick={handleNavItemClick}
                                        />
                                    ))}
                                </Box>
                                {groupIndex < SECTION_GROUPS.length - 1 && <Divider my="xs" mx="md" />}
                            </Box>
                        ))}
                    </AppShell.Section>
                </>
            ) : null}
        </>
    );
};
